import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

import { me } from "../api/endpoints";
import type { UserProfile } from "../api/types";
import { useActingClinic } from "../hooks/useActingClinic";
import { home } from "../lib/home";
import ActingBanner from "./ActingBanner";
import {
  AdminIcon,
  BoardIcon,
  CalendarIcon,
  ChamberIcon,
  DashboardIcon,
  MenuIcon,
  PatientsIcon,
  SessionsIcon,
} from "./Icon";
import UserMenu from "./UserMenu";

/**
 * The frame every staff screen sits in.
 *
 * Until now each screen carried its own way out — "Back to today", "Change
 * session", "← All sessions" — written by whoever added the page, and a
 * receptionist who arrived somewhere unexpected had a browser back button and
 * good luck. One frame means one answer: where am I, what else is there, and
 * who am I signed in as, in the same place on every screen.
 *
 * Three surfaces deliberately do **not** get this frame, and it matters that
 * they do not:
 *
 * - **The waiting-room boards.** A television on a wall with a navigation rail
 *   down the side of it is a television showing somebody's software. The board
 *   is a display, and its whole screen is the number.
 * - **The patient tracker.** It belongs to a patient on their own phone, who
 *   has no account here and nothing to navigate to.
 * - **Login and onboarding.** Nowhere to go until you are somebody.
 *
 * The rail is permanent on a desktop and a slide-over below 900px, which is
 * where the console tablet lives — nine hundred pixels of queue is worth more
 * to a receptionist mid-shift than a menu she uses twice a day.
 */
export default function AppShell() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const acting = useActingClinic();

  useEffect(() => {
    let cancelled = false;
    // A failure here is not worth an error state. The screen below is the page;
    // this is navigation, and a rail with one fewer link is better than an
    // error banner over a working console.
    void me()
      .then((who) => { if (!cancelled) setProfile(who); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  // Any navigation closes the slide-over. Without this, tapping a link on a
  // tablet leaves the menu sitting over the page you asked for.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const superadmin = profile?.role === "SUPERADMIN";

  // Every clinic screen is scoped to *a* clinic, and a superadmin belongs to
  // none — those pages answer 404 to them until they choose one to stand in.
  // Offering the links anyway was the rail advertising six dead ends.
  const inClinic = Boolean(profile?.clinic_id) || (superadmin && acting !== null);
  const administers = profile?.role === "CLINIC_ADMIN" || (superadmin && acting !== null);
  // Not a superadmin standing in a clinic: they have no doctor row there, so
  // "my chamber" is not a room they have.
  const consults = profile?.role === "DOCTOR" || profile?.role === "CLINIC_ADMIN";
  // The front desk's half of the app: which queues are running today, the
  // clinic-wide dashboard, and the television in the waiting room. A consultant
  // needs none of it — they have one room, and everything they do happens in
  // it. What is left for them is their chamber, their calendar, and the search.
  const runsTheFloor = profile?.role !== "DOCTOR";

  return (
    <div className={open ? "shell shell--menu-open" : "shell"}>
      <header className="shell__top">
        <button
          className="shell__menu"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => { setOpen((was) => !was); }}
        >
          <MenuIcon />
        </button>

        {/* Home is whatever this account's first screen is: a consultant's
            chamber, the desk's dashboard, and the onboarding console for a
            superadmin who has not chosen a clinic to stand in. */}
        <Link className="shell__brand" to={home(profile?.role, inClinic)}>
          <span className="shell__mark" aria-hidden="true" />
          LiveQueue
        </Link>

        <span className="shell__context">Clinic workspace</span>

        <UserMenu profile={profile} />
      </header>

      {/* Inside the frame rather than above it, so it is under the header on
          every screen a superadmin can reach while standing in somebody else's
          clinic — including the one page that is theirs. */}
      <ActingBanner />

      <nav className="shell__nav" aria-label="Main">
        <div className="shell__nav-heading">Workspace</div>
        {inClinic && (
          <>
            {runsTheFloor && (
              <>
                {/* Lit for `/` as well: that is the path the application
                    hostname's root rewrite lands on, and a rail that highlights
                    nothing is a rail that has lost track of where you are. */}
                <NavItem
                  to="/dashboard"
                  alsoActiveOn={location.pathname === "/"}
                  icon={<DashboardIcon />}
                  label="Dashboard"
                />
                <NavItem
                  to="/sessions"
                  alsoActiveOn={location.pathname.startsWith("/console/") || location.pathname.startsWith("/roster/")}
                  icon={<SessionsIcon />}
                  label="Sessions"
                />
              </>
            )}
            {consults && <NavItem to="/chamber" icon={<ChamberIcon />} label="My chamber" />}
            <NavItem to="/calendar" icon={<CalendarIcon />} label="Calendar" />
            <NavItem to="/patients" icon={<PatientsIcon />} label="Patients" />
            {runsTheFloor && (
              <NavItem to="/board" icon={<BoardIcon />} label="Waiting-room TV" />
            )}
          </>
        )}
        {administers && (
          <NavItem to="/admin/staff" alsoActiveOn={location.pathname.startsWith("/admin/")} icon={<AdminIcon />} label="Administration" />
        )}
        {/* Theirs whether or not they are standing in a clinic — it is where
            they come back to in order to leave one. */}
        {superadmin && <NavItem to="/onboard" icon={<AdminIcon />} label="Onboard a clinic" />}
        <div className="shell__nav-footer">
          <span className="shell__footer-mark" aria-hidden="true">+</span>
          <strong>A little less waiting.</strong>
          <span>More time for better care.</span>
        </div>
      </nav>

      {/* Closes the slide-over by tapping the page behind it, which is what
          everybody tries first. Inert on a desktop, where the rail is part of
          the layout rather than over it. */}
      <button
        className="shell__scrim"
        tabIndex={-1}
        aria-hidden="true"
        onClick={() => { setOpen(false); }}
      />

      <main className="shell__main">
        <Outlet />
      </main>
    </div>
  );
}

/**
 * `NavLink` rather than `Link`, for `aria-current` and the active style — both
 * from one source, so the rail cannot highlight one item while announcing
 * another.
 */
function NavItem({
  to,
  label,
  icon,
  alsoActiveOn = false,
}: {
  to: string;
  label: string;
  icon: ReactNode;
  alsoActiveOn?: boolean;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        isActive || alsoActiveOn ? "shell__link shell__link--on" : "shell__link"
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}
